import { z } from 'zod';

// Availability management schemas (Story #41)
export const AvailabilityTypeSchema = z.enum(['available', 'busy', 'maybe']);

export const RecurrencePatternSchema = z
  .object({
    type: z.enum(['weekly', 'monthly']),
    days: z.array(z.string()),
    until: z.date().optional(),
  })
  .optional();

// Time validation helpers
const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
const fifteenMinuteIncrements = /^([01]?[0-9]|2[0-3]):(00|15|30|45)$/;

export const DancerAvailabilitySchema = z
  .object({
    availableDate: z.date({
      required_error: 'Date is required',
      invalid_type_error: 'Invalid date format',
    }),
    startTime: z
      .string()
      .regex(timeRegex, 'Invalid time format (HH:MM)')
      .regex(
        fifteenMinuteIncrements,
        'Time must be in 15-minute increments (00, 15, 30, 45)',
      ),
    endTime: z
      .string()
      .regex(timeRegex, 'Invalid time format (HH:MM)')
      .regex(
        fifteenMinuteIncrements,
        'Time must be in 15-minute increments (00, 15, 30, 45)',
      ),
    availabilityType: AvailabilityTypeSchema.default('available'),
    notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
    isRecurring: z.boolean().default(false),
    recurrencePattern: RecurrencePatternSchema,
    timezone: z.string().default('UTC'),
  })
  .refine(
    (data) => {
      // Ensure end time is after start time
      const startParts = data.startTime.split(':').map(Number);
      const endParts = data.endTime.split(':').map(Number);

      if (startParts.length !== 2 || endParts.length !== 2) return false;

      const startHour = startParts[0];
      const startMin = startParts[1];
      const endHour = endParts[0];
      const endMin = endParts[1];

      if (
        startHour === undefined ||
        startMin === undefined ||
        endHour === undefined ||
        endMin === undefined
      )
        return false;
      if (
        isNaN(startHour) ||
        isNaN(startMin) ||
        isNaN(endHour) ||
        isNaN(endMin)
      )
        return false;

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      return endMinutes > startMinutes;
    },
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    },
  );

export const TimeSlotSchema = z
  .object({
    startTime: z
      .string()
      .regex(timeRegex, 'Invalid time format (HH:MM)')
      .regex(
        fifteenMinuteIncrements,
        'Time must be in 15-minute increments (00, 15, 30, 45)',
      ),
    endTime: z
      .string()
      .regex(timeRegex, 'Invalid time format (HH:MM)')
      .regex(
        fifteenMinuteIncrements,
        'Time must be in 15-minute increments (00, 15, 30, 45)',
      ),
    availabilityType: AvailabilityTypeSchema.default('available'),
    notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
    isRecurring: z.boolean().default(false),
    recurrencePattern: RecurrencePatternSchema,
    timezone: z.string().default('UTC'),
  })
  .refine(
    (data) => {
      // Ensure end time is after start time
      const startParts = data.startTime.split(':').map(Number);
      const endParts = data.endTime.split(':').map(Number);

      if (startParts.length !== 2 || endParts.length !== 2) return false;

      const startHour = startParts[0];
      const startMin = startParts[1];
      const endHour = endParts[0];
      const endMin = endParts[1];

      if (
        startHour === undefined ||
        startMin === undefined ||
        endHour === undefined ||
        endMin === undefined
      )
        return false;
      if (
        isNaN(startHour) ||
        isNaN(startMin) ||
        isNaN(endHour) ||
        isNaN(endMin)
      )
        return false;

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      return endMinutes > startMinutes;
    },
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    },
  );

export const BulkAvailabilitySchema = z.object({
  dateRange: z
    .object({
      from: z.date(),
      to: z.date(),
    })
    .refine((data) => data.to >= data.from, {
      message: 'End date must be after or equal to start date',
      path: ['to'],
    }),
  timeSlots: z
    .array(TimeSlotSchema)
    .min(1, 'At least one time slot is required'),
  applyToWeekdays: z.array(z.number().min(0).max(6)).default([]),
  overwriteExisting: z.boolean().default(false),
});

export const TeamAvailabilityQuerySchema = z
  .object({
    teamId: z.string().uuid('Invalid team ID format'),
    startDate: z.date(),
    endDate: z.date(),
    minDuration: z.number().min(15).default(90), // minutes
    minDancersRequired: z.number().min(1).default(1),
  })
  .refine((data) => data.endDate >= data.startDate, {
    message: 'End date must be after or equal to start date',
    path: ['endDate'],
  });

// Base TimeSlot schema for partial updates (without refinement)
const TimeSlotUpdateSchema = z.object({
  startTime: z
    .string()
    .regex(timeRegex, 'Invalid time format (HH:MM)')
    .regex(
      fifteenMinuteIncrements,
      'Time must be in 15-minute increments (00, 15, 30, 45)',
    )
    .optional(),
  endTime: z
    .string()
    .regex(timeRegex, 'Invalid time format (HH:MM)')
    .regex(
      fifteenMinuteIncrements,
      'Time must be in 15-minute increments (00, 15, 30, 45)',
    )
    .optional(),
  availabilityType: AvailabilityTypeSchema.optional(),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
  isRecurring: z.boolean().optional(),
  recurrencePattern: RecurrencePatternSchema.optional(),
  timezone: z.string().optional(),
});

export const AvailabilitySlotUpdateSchema = z.object({
  id: z.string().uuid('Invalid availability slot ID'),
  updates: TimeSlotUpdateSchema,
});

// Quick availability preset schemas
export const QuickAvailabilityPresetSchema = z.object({
  name: z.string().min(1, 'Preset name is required').max(50, 'Name too long'),
  timeSlots: z
    .array(
      z.object({
        startTime: z.string().regex(fifteenMinuteIncrements),
        endTime: z.string().regex(fifteenMinuteIncrements),
        availabilityType: AvailabilityTypeSchema,
      }),
    )
    .min(1, 'At least one time slot required'),
  weekdays: z.array(z.number().min(0).max(6)).default([1, 2, 3, 4, 5]), // Mon-Fri default
});

// Type exports
export type DancerAvailabilityData = z.infer<typeof DancerAvailabilitySchema>;
export type TimeSlotData = z.infer<typeof TimeSlotSchema>;
export type BulkAvailabilityData = z.infer<typeof BulkAvailabilitySchema>;
export type TeamAvailabilityQueryData = z.infer<
  typeof TeamAvailabilityQuerySchema
>;
export type AvailabilitySlotUpdateData = z.infer<
  typeof AvailabilitySlotUpdateSchema
>;
export type QuickAvailabilityPresetData = z.infer<
  typeof QuickAvailabilityPresetSchema
>;
export type AvailabilityType = z.infer<typeof AvailabilityTypeSchema>;
export type RecurrencePattern = z.infer<typeof RecurrencePatternSchema>;
